import { Component, Injector, ViewEncapsulation } from '@angular/core';
import { ClassNames, DateFormat, StatusColor, Styling } from '@lsq-frontend/common-ui';
import { Icons, ResponsiveService } from '@lsq-frontend/common-ui';
import { Funding } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { AnalyticsValue } from '../../../constants/analytics.constants';
import {
    GetFundingsRequest,
    GetFundingsResponse
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { GetFundingsService } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { DateRangeReportComponent } from '../base/date-range-report.component';
import { ReportsUserStateTypes } from '../reports.user-state-type';

@Component({
    selector: 'fundings',
    template: `
        <div
            [ngClass]="{ 'min-page-width-medium-up': !(dataSource?.loading$ | async) }"
            class="fundings-report-page"
        >
            <lsq-banner
                [titleText]="title"
                [subTitle]="(dataSource.data$ | async)?.lastUpdatedDate | lastUpdatedDate"
                [hasContent]="true"
                [reportName]="title"
                [analyticsName]="analyticsName"
                [service]="apiService"
                [hideExport]="dataSource.noData$ | async"
                [hideDatePicker]="!dataSource"
                [datePickerConfiguration]="datePickerConfiguration"
            >
            </lsq-banner>
            <div class="container-fluid page-container">
                <div class="row">
                    <div class="col">
                        <lsq-data-table-mobile
                            *ngIf="responsiveService.smBelow | async"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                        >
                            <ng-template #mobileCardHeader let-row="row">
                                <div class="fundings-mobile-card-header">
                                    <div class="mtc-header-content-left">
                                        <div class="mtc-header-title">
                                            {{ row.paymentAmount | lsqCurrency }}
                                        </div>
                                        <div class="mtc-header-sub-title">
                                            {{ row.fundingDate | date: dateFormat.LsqShortYear }}
                                        </div>
                                    </div>
                                    <div class="mtc-header-content-right">
                                        <div class="mtc-header-status-text">
                                            <span class="status-text">{{ row.status }}</span>
                                            <lsq-icon
                                                *ngIf="row.status"
                                                [style.padding-right]="'0.5rem'"
                                                [type]="icons.Circle"
                                                [color]="statusColor[row.status]"
                                            >
                                            </lsq-icon>
                                        </div>
                                    </div>
                                </div>
                            </ng-template>

                            <ng-template #mobileCardColumns let-row="row">
                                <div class="card-item">
                                    <div
                                        class="card-item-label"
                                        i18n="Payment type header for fundings report"
                                    >
                                        Payment Type
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.paymentType }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div
                                        class="card-item-label"
                                        i18n="Expenses header for fundings report"
                                    >
                                        Expenses
                                    </div>
                                    <div class="card-item-value">
                                        {{ row.expenses | currencyDash }}
                                    </div>
                                </div>

                                <div class="card-item">
                                    <div
                                        class="card-item-label"
                                        i18n="Bank Account header for fundings report"
                                    >
                                        Bank Account
                                    </div>
                                    <div class="card-item-value text-truncate">
                                        {{ row.bankAccount }}
                                    </div>
                                </div>
                            </ng-template>
                        </lsq-data-table-mobile>

                        <lsq-data-table
                            *ngIf="!(responsiveService.smBelow | async)"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [hasStickyHeader]="true"
                            (takeChanged)="handleTakeChange($event)"
                        >
                            <lsq-row-count-column></lsq-row-count-column>

                            <lsq-date-column
                                [column]="{
                                    header: dateHeader,
                                    key: 'fundingDate',
                                    className: 'funding-date'
                                }"
                            >
                            </lsq-date-column>

                            <lsq-column
                                [column]="{
                                    header: paymentTypeBreakHeader,
                                    key: 'paymentType',
                                    className: 'payment-type'
                                }"
                            >
                            </lsq-column>

                            <lsq-currency-column
                                [column]="{
                                    header: amountHeader,
                                    key: 'paymentAmount',
                                    className: 'funding-amount',
                                    width: styling.TableColumn_TightWidth
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [column]="{
                                    header: expensesHeader,
                                    key: 'expenses',
                                    className: 'expenses',
                                    headerPopoverContent: expensesHeaderPopoverI18n,
                                    width: styling.TableColumn_TightWidth
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-column
                                [column]="{
                                    header: bankAccountBreakHeader,
                                    key: 'bankAccount',
                                    className: 'bank-account-name ' + classNames.TextTruncate
                                }"
                            >
                            </lsq-column>

                            <lsq-column
                                [column]="{
                                    header: statusHeader,
                                    key: 'status',
                                    className: 'funding-status'
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <div [ngClass]="['funding-status']">
                                        <lsq-icon
                                            *ngIf="row.status"
                                            [style.padding-right]="'0.5rem'"
                                            [type]="icons.Circle"
                                            [color]="statusColor[row.status]"
                                        >
                                        </lsq-icon>
                                        <span class="status-text">{{ row.status }}</span>
                                    </div>
                                </ng-template>
                            </lsq-column>
                        </lsq-data-table>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./fundings.report.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FundingsReportComponent extends DateRangeReportComponent {
    public title = this._i18n({
        value: 'Fundings',
        meaning: 'Fundings report title'
    });
    public analyticsName = AnalyticsValue.Funding;
    public userStateType = ReportsUserStateTypes.GetFundingsRequest;
    public savedDateFilterName = null;

    public dateHeader = this._i18n({
        value: 'Date',
        meaning: 'Funding date',
        description: 'Table column header'
    });
    public paymentTypeBreakHeader = this._i18n({
        value: 'Payment<br />Type',
        meaning: 'Payment type (wire or ACH)',
        description: 'Table column header'
    });
    public amountHeader = this._i18n({
        value: 'Amount',
        meaning: 'Funding amount',
        description: 'Table column header'
    });
    public expensesHeader = this._i18n({
        value: 'Expenses',
        meaning: 'Fees associated with the funding',
        description: 'Table column header'
    });
    public bankAccountBreakHeader = this._i18n({
        value: 'Bank<br />Account',
        description: 'Table column header'
    });
    public statusHeader = this._i18n({
        value: 'Status',
        meaning: 'Funding status',
        description: 'Table column header'
    });
    public expensesHeaderPopoverI18n = this._i18n({
        value: 'Wire fees, Tax Guard fees, UCC filing fees, due diligence fees, etc.',
        description: 'Tooltip definition of "expenses"'
    });

    // Constants
    public classNames = ClassNames;
    public dateFormat = DateFormat;
    public icons = Icons;
    public statusColor = StatusColor;
    public styling = Styling;

    public dataSource = new LsqApiServiceListSource<
        GetFundingsResponse,
        GetFundingsRequest,
        Funding
    >(this.apiService);

    constructor(
        private _i18n: I18n,
        public apiService: GetFundingsService,
        public responsiveService: ResponsiveService,
        public injector: Injector
    ) {
        super(injector);

        this.dispatchInitialRequest();
    }
}
